import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Linkedin, Instagram, Facebook, X as XIcon } from 'lucide-react';
function PolicyModal({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-150">
      <motion.div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-151" />
      <motion.div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      ease: [0.25, 0.46, 0.45, 0.94],
      duration: 0.35
    }} data-id="element-152">
        <div className="flex items-center justify-between px-8 py-5 border-b border-[#E8E4DC]" data-id="element-153">
          <h2 className="font-jakarta text-xl font-extrabold text-[#0F0E0C]" data-id="element-154">
            {title}
          </h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#F5F4F0] flex items-center justify-center hover:bg-[#E8E4DC] transition-colors" data-id="element-155">
            <XIcon className="w-4 h-4 text-[#5C5A55]" data-id="element-156" />
          </button>
        </div>
        <div className="overflow-y-auto px-8 py-6 text-sm text-[#5C5A55] font-inter leading-relaxed space-y-4" data-id="element-157">
          {children}
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <motion.footer ref={ref} initial={{
      opacity: 0,
      y: 20
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.6
    }} className="bg-[#0F0E0C] text-white pt-16 pb-8 relative overflow-hidden" data-id="element-158">
        {/* Subtle textile pattern */}
        <div className="absolute inset-0 thread-pattern opacity-20 pointer-events-none" data-id="element-159" />

        {/* Animated top border */}
        <motion.div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E8500A]/50 to-transparent" initial={{
        scaleX: 0
      }} animate={isInView ? {
        scaleX: 1
      } : {}} transition={{
        duration: 1.2,
        ease: 'easeInOut'
      }} data-id="element-160" />

        <div className="max-w-6xl mx-auto px-5 lg:px-8 relative z-10" data-id="element-161">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12" data-id="element-162">
            {/* Brand column */}
            <motion.div className="lg:col-span-2" initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            delay: 0.1
          }} data-id="element-163">
              <img src="https://cdn.magicpatterns.com/uploads/gPkoxER8MRU5GSVk7bdedN/LogoBlack.fdf2fa00.svg" alt="The Yarn Bazaar" className="h-8 w-auto mb-4 brightness-0 invert" data-id="element-164" />
              <p className="text-white/50 text-sm font-inter max-w-xs mb-5 leading-relaxed" data-id="element-165">
                India's leading B2B yarn marketplace connecting mills, traders,
                and exporters across the globe.
              </p>
              <p className="text-white/30 text-xs font-inter mb-4 leading-relaxed" data-id="element-166">
                A-3104 DB Woods Opp Laxchandi Heights,
                <br data-id="element-167" />
                Gokuldham Goregaon (East),
                <br data-id="element-168" />
                Mumbai 400063, Maharashtra, India
              </p>
              <div className="flex gap-3" data-id="element-169">
                <a href="https://in.linkedin.com/company/theyarnbazaar" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/[0.06] hover:bg-[#E8500A] flex items-center justify-center transition-colors duration-200" data-id="element-170">
                  <Linkedin className="w-4 h-4" data-id="element-171" />
                </a>
                <a href="https://www.instagram.com/theyarnbazaar/?hl=en" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/[0.06] hover:bg-[#E8500A] flex items-center justify-center transition-colors duration-200" data-id="element-172">
                  <Instagram className="w-4 h-4" data-id="element-173" />
                </a>
                <a href="https://www.facebook.com/theyarnbazaar/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/[0.06] hover:bg-[#E8500A] flex items-center justify-center transition-colors duration-200" data-id="element-174">
                  <Facebook className="w-4 h-4" data-id="element-175" />
                </a>
              </div>
            </motion.div>

            {/* Company column */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            delay: 0.2
          }} data-id="element-176">
              <h4 className="font-jakarta font-bold text-white text-sm mb-4 tracking-wide" data-id="element-177">
                Company
              </h4>
              <ul className="space-y-2.5 text-sm text-white/50" data-id="element-178">
                <li data-id="element-179">
                  <a href="#investors" className="hover:text-white transition-colors nav-link-thread inline-block" data-id="element-180">
                    Investors
                  </a>
                </li>
                <li data-id="element-181">
                  <a href="#marketplace" className="hover:text-white transition-colors nav-link-thread inline-block" data-id="element-182">
                    Marketplace
                  </a>
                </li>
                <li data-id="element-183">
                  <a href="https://in.linkedin.com/company/theyarnbazaar" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors nav-link-thread inline-block" data-id="element-184">
                    Careers
                  </a>
                </li>
              </ul>
            </motion.div>

            {/* Legal column */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            delay: 0.3
          }} data-id="element-185">
              <h4 className="font-jakarta font-bold text-white text-sm mb-4 tracking-wide" data-id="element-186">
                Legal
              </h4>
              <ul className="space-y-2.5 text-sm text-white/50" data-id="element-187">
                <li data-id="element-188">
                  <button onClick={() => setShowPrivacy(true)} className="hover:text-white transition-colors nav-link-thread inline-block text-left" data-id="element-189">
                    Privacy Policy
                  </button>
                </li>
                <li data-id="element-190">
                  <button onClick={() => setShowTerms(true)} className="hover:text-white transition-colors nav-link-thread inline-block text-left" data-id="element-191">
                    Terms & Conditions
                  </button>
                </li>
              </ul>
            </motion.div>
          </div>

          <div className="border-t border-white/[0.08] pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-192">
            <p className="text-xs text-white/30 font-inter" data-id="element-193">
              © 2026 Filosha Infotech Private Limited. All rights reserved.
            </p>
            <div className="flex items-center gap-2" data-id="element-194">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-195" />
              <span className="text-xs text-white/30 font-inter" data-id="element-196">
                Mumbai, India
              </span>
            </div>
          </div>
        </div>
      </motion.footer>

      {/* Privacy Policy Modal */}
      <AnimatePresence data-id="element-197">
        {showPrivacy && <PolicyModal title="Privacy Policy" onClose={() => setShowPrivacy(false)} data-id="element-198">
            <p className="text-[#0F0E0C] font-semibold" data-id="element-199">
              Filosha Infotech Private Limited — Privacy Policy
            </p>
            <p data-id="element-200">Last updated: January 2026</p>
            <p data-id="element-201">
              Filosha Infotech Private Limited ("The Yarn Bazaar", "we", "us",
              or "our") operates the platform accessible at theyarnbazaar.com.
              This Privacy Policy describes how we collect, use, and share
              information about you when you use our services.
            </p>
            <p data-id="element-202">
              <strong data-id="element-203">Information We Collect</strong>
              <br data-id="element-204" />
              We collect information you provide directly to us, such as when
              you create an account, request a demo, or contact us. This
              includes your name, email address, phone number, company name, and
              role.
            </p>
            <p data-id="element-205">
              <strong data-id="element-206">How We Use Your Information</strong>
              <br data-id="element-207" />
              We use the information we collect to provide, maintain, and
              improve our services, process transactions, send you technical
              notices and support messages, and respond to your comments and
              questions.
            </p>
            <p data-id="element-208">
              <strong data-id="element-209">Information Sharing</strong>
              <br data-id="element-210" />
              We do not sell, trade, or otherwise transfer your personally
              identifiable information to outside parties except to trusted
              third parties who assist us in operating our platform, conducting
              our business, or servicing you, so long as those parties agree to
              keep this information confidential.
            </p>
            <p data-id="element-211">
              <strong data-id="element-212">Data Security</strong>
              <br data-id="element-213" />
              We implement appropriate technical and organizational measures to
              protect the security of your personal information against
              unauthorized access, alteration, disclosure, or destruction.
            </p>
            <p data-id="element-214">
              <strong data-id="element-215">Contact Us</strong>
              <br data-id="element-216" />
              If you have any questions about this Privacy Policy, please
              contact us at: Filosha Infotech Private Limited, A-3104 DB Woods
              Opp Laxchandi Heights, Gokuldham Goregaon (East), Mumbai 400063,
              Maharashtra, India.
            </p>
          </PolicyModal>}
      </AnimatePresence>

      {/* Terms & Conditions Modal */}
      <AnimatePresence data-id="element-217">
        {showTerms && <PolicyModal title="Terms & Conditions" onClose={() => setShowTerms(false)} data-id="element-218">
            <p className="text-[#0F0E0C] font-semibold" data-id="element-219">
              Filosha Infotech Private Limited — Terms & Conditions
            </p>
            <p data-id="element-220">Last updated: January 2026</p>
            <p data-id="element-221">
              By accessing and using The Yarn Bazaar platform operated by
              Filosha Infotech Private Limited, you accept and agree to be bound
              by the terms and provisions of this agreement.
            </p>
            <p data-id="element-222">
              <strong data-id="element-223">Use of Platform</strong>
              <br data-id="element-224" />
              The Yarn Bazaar is a B2B marketplace platform for yarn trading.
              Users must be registered businesses or individuals engaged in the
              textile industry. You agree to use the platform only for lawful
              purposes and in accordance with these Terms.
            </p>
            <p data-id="element-225">
              <strong data-id="element-226">Account Responsibilities</strong>
              <br data-id="element-227" />
              You are responsible for maintaining the confidentiality of your
              account credentials and for all activities that occur under your
              account. You agree to notify us immediately of any unauthorized
              use of your account.
            </p>
            <p data-id="element-228">
              <strong data-id="element-229">Transactions</strong>
              <br data-id="element-230" />
              The Yarn Bazaar facilitates connections between buyers and
              sellers. All transactions are between the respective parties. We
              are not responsible for the quality, safety, or legality of items
              listed, the truth or accuracy of listings, or the ability of
              sellers to sell or buyers to buy.
            </p>
            <p data-id="element-231">
              <strong data-id="element-232">Intellectual Property</strong>
              <br data-id="element-233" />
              The platform and its original content, features, and functionality
              are owned by Filosha Infotech Private Limited and are protected by
              international copyright, trademark, and other intellectual
              property laws.
            </p>
            <p data-id="element-234">
              <strong data-id="element-235">Limitation of Liability</strong>
              <br data-id="element-236" />
              Filosha Infotech Private Limited shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages
              resulting from your use of or inability to use the platform.
            </p>
            <p data-id="element-237">
              <strong data-id="element-238">Governing Law</strong>
              <br data-id="element-239" />
              These Terms shall be governed by and construed in accordance with
              the laws of India, with jurisdiction in Mumbai, Maharashtra.
            </p>
            <p data-id="element-240">
              <strong data-id="element-241">Contact Us</strong>
              <br data-id="element-242" />
              For any questions regarding these Terms, contact us at: Filosha
              Infotech Private Limited, A-3104 DB Woods Opp Laxchandi Heights,
              Gokuldham Goregaon (East), Mumbai 400063, Maharashtra, India.
            </p>
          </PolicyModal>}
      </AnimatePresence>
    </>;
}